import React, { useState, useEffect, useRef } from 'react';
import { X, MessageCircle, Send, Trash2 } from 'lucide-react';
import axios from 'axios';
import { normalizeOptions } from '../utils/sheetOptions';

// --- Helpers ---

const timeAgo = (date) => {
  const seconds = Math.floor((new Date() - new Date(date)) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

const parseCellValue = (cell) => {
  if (!cell?.value) return null;
  try {
    return typeof cell.value === 'string' ? JSON.parse(cell.value) : cell.value;
  } catch {
    return cell.value;
  }
};

function getDisplayVal(cell) {
  if (!cell) return '';
  if (cell.display_val != null) return String(cell.display_val);
  const raw = parseCellValue(cell);
  return raw != null ? String(raw) : '';
}

function isReadOnlyColumn(col) {
  return col.is_system || col.type === 'auto_number' || col.type === 'formula' || col.system_type;
}

// --- Field Editors ---

function TextField({ value, multiline, onBlur }) {
  const [local, setLocal] = useState(value || '');

  useEffect(() => {
    setLocal(value || '');
  }, [value]);

  const inputStyle = {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '14px',
    color: '#111827',
    outline: 'none',
    boxSizing: 'border-box',
    resize: multiline ? 'vertical' : 'none',
    fontFamily: 'inherit',
    lineHeight: '1.5'
  };

  if (multiline) {
    return React.createElement('textarea', {
      value: local,
      rows: 3,
      style: inputStyle,
      onChange: (e) => setLocal(e.target.value),
      onBlur: () => onBlur(local)
    });
  }

  return React.createElement('input', {
    type: 'text',
    value: local,
    style: inputStyle,
    onChange: (e) => setLocal(e.target.value),
    onBlur: () => onBlur(local)
  });
}

function NumberField({ value, onBlur }) {
  const [local, setLocal] = useState(value != null ? String(value) : '');

  useEffect(() => {
    setLocal(value != null ? String(value) : '');
  }, [value]);

  return React.createElement('input', {
    type: 'number',
    value: local,
    style: {
      width: '100%',
      padding: '8px 12px',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '14px',
      color: '#111827',
      outline: 'none',
      boxSizing: 'border-box'
    },
    onChange: (e) => setLocal(e.target.value),
    onBlur: () => onBlur(local !== '' ? Number(local) : null)
  });
}

function DateField({ value, onBlur }) {
  const [local, setLocal] = useState(value || '');

  useEffect(() => {
    setLocal(value || '');
  }, [value]);

  return React.createElement('input', {
    type: 'date',
    value: local,
    style: {
      width: '100%',
      padding: '8px 12px',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '14px',
      color: '#111827',
      outline: 'none',
      boxSizing: 'border-box'
    },
    onChange: (e) => setLocal(e.target.value),
    onBlur: () => onBlur(local || null)
  });
}

function DropdownField({ column, value, onBlur }) {
  const options = normalizeOptions(column.config?.options);
  const initial = value != null ? String(value) : '';
  const [local, setLocal] = useState(initial);

  useEffect(() => {
    setLocal(value != null ? String(value) : '');
  }, [value]);

  return React.createElement('select', {
    value: local,
    style: {
      width: '100%',
      padding: '8px 12px',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '14px',
      color: '#111827',
      outline: 'none',
      boxSizing: 'border-box',
      backgroundColor: '#fff'
    },
    onChange: (e) => {
      const next = e.target.value;
      setLocal(next);
      onBlur(next || null);
    },
    onBlur: () => onBlur(local || null)
  },
    React.createElement('option', { value: '' }, '—'),
    options.map((opt) => (
      React.createElement('option', { key: opt.id, value: opt.id }, opt.label)
    ))
  );
}

function MultiSelectField({ column, value, onBlur }) {
  const options = normalizeOptions(column.config?.options);
  // value may be: null, a JSON-encoded string of an array, or an array
  let current = [];
  if (Array.isArray(value)) current = value.map(String);
  else if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) current = parsed.map(String);
    } catch (_e) {
      current = value ? [value] : [];
    }
  }

  const [selected, setSelected] = React.useState(current);

  React.useEffect(() => {
    setSelected(current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      onBlur(next);
      return next;
    });
  };

  return React.createElement('div', {
    style: { display: 'flex', flexWrap: 'wrap', gap: '6px', padding: '4px 0' }
  },
    options.map((opt) => {
      const isSelected = selected.includes(opt.id);
      return React.createElement('button', {
        key: opt.id,
        type: 'button',
        onClick: () => toggle(opt.id),
        style: {
          padding: '4px 10px',
          background: isSelected ? opt.color : 'transparent',
          color: isSelected ? '#fff' : '#374151',
          border: `1px solid ${isSelected ? opt.color : '#d1d5db'}`,
          borderRadius: '999px',
          fontSize: '12px',
          fontWeight: 600,
          cursor: 'pointer',
          transition: 'background 0.15s, color 0.15s'
        }
      }, opt.label);
    }),
    options.length === 0 && React.createElement('span', {
      style: { fontSize: '12px', color: '#9ca3af', fontStyle: 'italic' }
    }, 'No labels defined. Manage labels in Plan Settings.')
  );
}

function CheckboxField({ value, onChange }) {
  const checked = Boolean(value);

  return React.createElement('div', {
    style: { display: 'flex', alignItems: 'center', height: '38px' }
  },
    React.createElement('input', {
      type: 'checkbox',
      checked: checked,
      style: { width: '18px', height: '18px', cursor: 'pointer', accentColor: '#7c3aed' },
      onChange: (e) => onChange(e.target.checked)
    })
  );
}

function ContactField({ value, contacts, onBlur }) {
  const [local, setLocal] = useState(value != null ? String(value) : '');

  useEffect(() => {
    setLocal(value != null ? String(value) : '');
  }, [value]);

  return React.createElement('select', {
    value: local,
    style: {
      width: '100%',
      padding: '8px 12px',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '14px',
      color: '#111827',
      outline: 'none',
      boxSizing: 'border-box',
      backgroundColor: '#fff'
    },
    onChange: (e) => setLocal(e.target.value),
    onBlur: () => onBlur(local || null)
  },
    React.createElement('option', { value: '' }, '—'),
    (contacts || []).map((c) =>
      React.createElement('option', { key: c.id, value: String(c.id) }, c.name || c.email || String(c.id))
    )
  );
}

function ReadOnlyField({ value }) {
  return React.createElement('div', {
    style: {
      width: '100%',
      padding: '8px 12px',
      border: '1px solid #e5e7eb',
      borderRadius: '6px',
      fontSize: '14px',
      color: '#6b7280',
      backgroundColor: '#f9fafb',
      minHeight: '38px',
      boxSizing: 'border-box'
    }
  }, value || '');
}

// --- FieldRow ---

function FieldRow({ col, cell, canEdit, contacts, onSave }) {
  const readOnly = isReadOnlyColumn(col) || !canEdit;
  const rawValue = parseCellValue(cell);
  const displayValue = getDisplayVal(cell);

  const isMultilineText = col.type === 'text' && (displayValue.length > 60 || (displayValue || '').includes('\n'));
  const spanFull = isMultilineText;

  const handleSave = (newVal) => {
    if (newVal !== rawValue) {
      onSave(col.id, newVal);
    }
  };

  let editor;
  if (readOnly) {
    editor = React.createElement(ReadOnlyField, { value: displayValue });
  } else if (col.type === 'number') {
    editor = React.createElement(NumberField, { value: rawValue, onBlur: handleSave });
  } else if (col.type === 'date') {
    editor = React.createElement(DateField, { value: rawValue, onBlur: handleSave });
  } else if (col.type === 'dropdown') {
    editor = React.createElement(DropdownField, { column: col, value: rawValue, onBlur: handleSave });
  } else if (col.type === 'multi_select') {
    editor = React.createElement(MultiSelectField, { column: col, value: rawValue, onBlur: handleSave });
  } else if (col.type === 'checkbox') {
    editor = React.createElement(CheckboxField, {
      value: rawValue,
      onChange: (checked) => onSave(col.id, checked)
    });
  } else if (col.type === 'contact') {
    editor = React.createElement(ContactField, { value: rawValue, contacts, onBlur: handleSave });
  } else {
    // text (default)
    editor = React.createElement(TextField, { value: displayValue, multiline: isMultilineText, onBlur: handleSave });
  }

  return React.createElement('div', {
    style: {
      gridColumn: spanFull ? '1 / -1' : undefined,
      display: 'flex',
      flexDirection: 'column',
      gap: '4px'
    }
  },
    React.createElement('label', {
      style: {
        fontSize: '12px',
        fontWeight: 500,
        color: '#6b7280',
        marginBottom: '4px',
        display: 'block'
      }
    }, col.name),
    editor
  );
}

// --- Comments Section ---

function CommentsSection({ rowId, currentUser, canComment }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    if (!rowId) return;
    setLoading(true);
    axios.get(`/api/sheets/rows/${rowId}/comments`)
      .then(res => setComments(res.data || []))
      .catch(err => console.error('Failed to load comments:', err))
      .finally(() => setLoading(false));
  }, [rowId]);

  const handlePost = async () => {
    if (!newComment.trim()) return;
    setPosting(true);
    try {
      const res = await axios.post(`/api/sheets/rows/${rowId}/comments`, { content: newComment.trim() });
      const created = res.data;
      setComments(prev => [...prev, created]);
      setNewComment('');
    } catch (err) {
      console.error('Failed to post comment:', err);
    } finally {
      setPosting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      handlePost();
    }
  };

  const authorInitial = (name) => {
    if (!name) return '?';
    return name.charAt(0).toUpperCase();
  };

  return React.createElement('div', {
    style: {
      marginTop: '24px',
      paddingTop: '20px',
      borderTop: '1px solid #e5e7eb'
    }
  },
    // Header
    React.createElement('div', {
      style: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '16px'
      }
    },
      React.createElement(MessageCircle, { size: 16, color: '#6b7280' }),
      React.createElement('span', {
        style: { fontSize: '14px', fontWeight: 600, color: '#374151' }
      }, 'Comments'),
      comments.length > 0 && React.createElement('span', {
        style: {
          fontSize: '11px',
          backgroundColor: '#e5e7eb',
          color: '#6b7280',
          padding: '1px 7px',
          borderRadius: '999px',
          fontWeight: 600
        }
      }, comments.length)
    ),

    // Loading
    loading && React.createElement('div', {
      style: { fontSize: '13px', color: '#9ca3af', padding: '8px 0' }
    }, 'Loading comments...'),

    // Comment list
    !loading && comments.map((comment, i) => {
      const authorName = comment.author_name || comment.user_name || (currentUser && currentUser.name) || 'Unknown';
      return React.createElement('div', {
        key: comment.id || i,
        style: {
          display: 'flex',
          gap: '12px',
          marginBottom: '16px'
        }
      },
        // Avatar
        React.createElement('div', {
          style: {
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            backgroundColor: '#e5e7eb',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 600,
            fontSize: '13px',
            color: '#374151',
            flexShrink: 0
          }
        }, authorInitial(authorName)),

        // Content
        React.createElement('div', { style: { flex: 1 } },
          React.createElement('div', {
            style: { display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '4px' }
          },
            React.createElement('span', {
              style: { fontWeight: 500, fontSize: '13px', color: '#111827' }
            }, authorName),
            React.createElement('span', {
              style: { fontSize: '11px', color: '#9ca3af' }
            }, timeAgo(comment.created_at || comment.createdAt))
          ),
          React.createElement('div', {
            style: { fontSize: '14px', color: '#374151', lineHeight: '1.5' }
          }, comment.content)
        )
      );
    }),

    !loading && comments.length === 0 && React.createElement('div', {
      style: { fontSize: '13px', color: '#9ca3af', fontStyle: 'italic', marginBottom: '16px' }
    }, 'No comments yet.'),

    // Add comment
    canComment && React.createElement('div', {
      style: { display: 'flex', gap: '8px', alignItems: 'flex-end', marginTop: '8px' }
    },
      React.createElement('textarea', {
        value: newComment,
        placeholder: 'Add a comment... (Ctrl+Enter to post)',
        rows: 2,
        style: {
          flex: 1,
          padding: '8px 12px',
          border: '1px solid #d1d5db',
          borderRadius: '6px',
          fontSize: '14px',
          fontFamily: 'inherit',
          resize: 'vertical',
          outline: 'none',
          lineHeight: '1.5'
        },
        onChange: (e) => setNewComment(e.target.value),
        onKeyDown: handleKeyDown
      }),
      React.createElement('button', {
        disabled: posting || !newComment.trim(),
        onClick: handlePost,
        style: {
          padding: '8px 16px',
          backgroundColor: posting || !newComment.trim() ? '#d1d5db' : '#7c3aed',
          color: '#fff',
          border: 'none',
          borderRadius: '6px',
          fontSize: '14px',
          fontWeight: 500,
          cursor: posting || !newComment.trim() ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          flexShrink: 0,
          transition: 'background-color 0.15s'
        }
      },
        React.createElement(Send, { size: 14 }),
        'Post'
      )
    )
  );
}

// --- SheetRowDetail ---

export default function SheetRowDetail({
  row,
  columns,
  sheet,
  onCellUpdate,
  onClose,
  canEdit,
  contacts,
  currentUser,
  sprints,
  onMoveToSprint,
  onDelete
}) {
  const overlayRef = useRef(null);

  if (!row) return null;

  const visibleColumns = (columns || []).filter(col => !col.hidden);

  // Primary column value for title
  const primaryCol = visibleColumns.find(c => !c.is_system && !c.system_type);
  const titleValue = primaryCol
    ? getDisplayVal(row.cells && row.cells[primaryCol.id])
    : '';

  const handleCellSave = async (colId, newVal) => {
    try {
      const encoded = JSON.stringify(newVal);
      await onCellUpdate(row.id, { [colId]: encoded });
    } catch (err) {
      console.error('Failed to save cell:', err);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) {
      onClose();
    }
  };

  const canComment = canEdit || true; // Show comment input for all users who can view

  return React.createElement('div', {
    ref: overlayRef,
    onClick: handleOverlayClick,
    style: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      zIndex: 1000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }
  },
    React.createElement('div', {
      style: {
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        maxWidth: '600px',
        width: '100%',
        maxHeight: '85vh',
        overflowY: 'auto',
        padding: 0,
        display: 'flex',
        flexDirection: 'column'
      },
      onClick: (e) => e.stopPropagation()
    },
      // Header
      React.createElement('div', {
        style: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '20px 24px',
          borderBottom: '1px solid #e5e7eb',
          flexShrink: 0
        }
      },
        React.createElement('div', null,
          React.createElement('div', {
            style: { fontSize: '16px', fontWeight: 600, color: '#111827', marginBottom: '2px' }
          }, titleValue || React.createElement('span', { style: { color: '#9ca3af', fontStyle: 'italic' } }, 'Untitled')),
          sheet && React.createElement('div', {
            style: { fontSize: '12px', color: '#9ca3af' }
          }, sheet.name)
        ),
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: '4px' } },
          canEdit && onDelete && React.createElement('button', {
            onClick: () => {
              if (window.confirm('Delete this card? This cannot be undone.')) {
                onDelete();
              }
            },
            title: 'Delete card',
            style: {
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '6px',
              color: '#9ca3af',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'color 0.15s, background-color 0.15s'
            },
            onMouseEnter: (e) => {
              e.currentTarget.style.color = '#b91c1c';
              e.currentTarget.style.backgroundColor = '#fee2e2';
            },
            onMouseLeave: (e) => {
              e.currentTarget.style.color = '#9ca3af';
              e.currentTarget.style.backgroundColor = 'transparent';
            }
          },
            React.createElement(Trash2, { size: 18 })
          ),
          React.createElement('button', {
            onClick: onClose,
            title: 'Close',
            style: {
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '6px',
              color: '#9ca3af',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'color 0.15s, background-color 0.15s'
            },
            onMouseEnter: (e) => {
              e.currentTarget.style.color = '#374151';
              e.currentTarget.style.backgroundColor = '#f3f4f6';
            },
            onMouseLeave: (e) => {
              e.currentTarget.style.color = '#9ca3af';
              e.currentTarget.style.backgroundColor = 'transparent';
            }
          },
            React.createElement(X, { size: 20 })
          )
        )
      ),

      // Body
      React.createElement('div', {
        style: { padding: '24px', flex: 1 }
      },
        // Field grid
        React.createElement('div', {
          style: {
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '16px'
          }
        },
          visibleColumns.map(col =>
            React.createElement(FieldRow, {
              key: col.id,
              col,
              cell: row.cells && row.cells[col.id],
              canEdit,
              contacts,
              onSave: handleCellSave
            })
          )
        ),

        // Sprint assignment
        Array.isArray(sprints) && onMoveToSprint && React.createElement('div', {
          style: { marginTop: '20px', padding: '12px 14px', background: '#f8fafc', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '10px' }
        },
          React.createElement('span', {
            style: { fontSize: '0.75rem', fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }
          }, 'Sprint'),
          React.createElement('select', {
            value: row.sprint_id == null ? '' : String(row.sprint_id),
            onChange: (e) => {
              const v = e.target.value;
              onMoveToSprint(v === '' ? null : parseInt(v, 10));
            },
            style: { flex: 1, padding: '6px 10px', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '0.85rem', background: '#ffffff' }
          },
            React.createElement('option', { value: '' }, 'Backlog'),
            sprints.map((s) => React.createElement('option', { key: s.id, value: String(s.id) }, s.name))
          )
        ),

        // Comments
        React.createElement(CommentsSection, {
          rowId: row.id,
          currentUser,
          canComment
        })
      )
    )
  );
}
